import { faBallotCheck } from "@fortawesome/pro-regular-svg-icons/faBallotCheck";
import { Box } from "@mittwald/flow-components/dist/components/Box";
import { ColumnLayout } from "@mittwald/flow-components/dist/components/ColumnLayout";
import { Icon } from "@mittwald/flow-components/dist/components/Icon";
import { Section } from "@mittwald/flow-components/dist/components/Section";
import { Text } from "@mittwald/flow-components/dist/components/Text";
import TranslationProvider from "@mittwald/flow-components/dist/components/TranslationProvider/TranslationProvider";
import { useTranslation } from "@mittwald/flow-components/dist/hooks/useTranslation";
import { extractTextFromReactNode } from "@mittwald/flow-components/dist/lib/extractTextFromReactNode";
import { joinTranslationKeys } from "@mittwald/flow-components/dist/lib/translation";
import { iconBoxesStacked } from "@mittwald/flow-icons/dist/boxesStacked";
import { iconTruckFast } from "@mittwald/flow-icons/dist/truckFast";
import { iconUser } from "@mittwald/flow-icons/dist/user";
import React, { useEffect } from "react";
import { H3 } from "@mittwald/flow-components/dist/components/H3";
import { ConditionsCheckbox } from "../../../../../../components/ConditionsCheckbox/ConditionsCheckbox";
import type {
  RelocationFormData,
  RelocationReferrer,
} from "../../../RelocationRequestWidget";
import {
  additionalDataComparePrice,
  domainTransferPricePerDomain,
  getArticleByName,
} from "../../../utils/articles";
import { OverviewBox } from "../../OverviewBox/OverviewBox";
import { useFormContext } from "@mittwald/flow-components/dist/components/Form";
import { getNeededRelocationDaysCount } from "./utils/getNeededRelocationDaysCount";
import { getAvatarImageWidth } from "@mittwald/flow-components/dist/components/Avatar/lib";
import { Section as NewSection } from "@mittwald/flow-react-components";

interface OverviewStepContentProps {
  compact?: boolean;
  referrer: RelocationReferrer;
}

export const OverviewStepContent: React.FC<OverviewStepContentProps> = (
  props,
) => {
  const iconWidth = getAvatarImageWidth("m");
  const { compact } = props;
  const form = useFormContext<RelocationFormData>();
  const translate = useTranslation();
  const {
    articleType,
    additionalServices,
    domains,
    target,
    provider,
    contact,
  } = form.watch();
  const translatedArticleName = extractTextFromReactNode(
    translate(
      joinTranslationKeys(
        "component",
        "orderOverview",
        "relocation",
        "type",
        articleType,
      ),
    ),
  );

  useEffect(() => {
    const positions = [
      {
        name: translatedArticleName,
        price: getArticleByName(articleType).price,
      },
    ];

    if (additionalServices.dataCompare === "additionalCompare") {
      positions.push({
        name: extractTextFromReactNode(
          translate(
            joinTranslationKeys(
              "component",
              "orderOverview",
              "relocation",
              "position",
              "additionalDataCompare",
            ),
          ),
        ),
        price: additionalDataComparePrice,
      });
    }

    if (domains !== undefined && domains.length > 0) {
      const domainTransferPrice = domains.length * domainTransferPricePerDomain;

      positions.push({
        name: extractTextFromReactNode(
          translate({
            id: joinTranslationKeys(
              "component",
              "orderOverview",
              "relocation",
              "position",
              "domainTransfer",
            ),
            values: {
              domainCount: domains.length,
            },
          }),
        ),
        price: domainTransferPrice,
      });
    }

    form.setValue("prices", {
      positions,
      total: positions.reduce((acc, position) => acc + position.price, 0),
    });
  }, []);

  const staticInformation = (
    <ColumnLayout _width="100%" medium={compact === true ? [1] : [1, 1, 1]}>
      <Box _itemGap="s">
        <Box _flexDirection="row" _itemGap="m">
          <Box _flexDirection="row" _justifyContent="center" _width={iconWidth}>
            <Icon _color="brand-primary-dark" _size="m" icon={iconTruckFast} />
          </Box>
          <Box>
            <H3 i18n="tariff" />
            <Text i18n={target.product} />
            <Text>{target.projectName}</Text>
            <Text>{target.organisation}</Text>
          </Box>
        </Box>
      </Box>
      <Box _itemGap="s">
        <Box _flexDirection="row" _itemGap="m">
          <Box _flexDirection="row" _justifyContent="center" _width={iconWidth}>
            <Icon
              _color="brand-primary-dark"
              _size="m"
              icon={iconBoxesStacked}
            />
          </Box>
          <Box>
            <H3 i18n="credentials" />
            <Text>{provider.name}</Text>
            <Text>{provider.userName}</Text>
            <Text ellipsis>{provider.loginUrl}</Text>
          </Box>
        </Box>
      </Box>
      <Box _itemGap="s">
        <Box _flexDirection="row" _itemGap="m">
          <Box _flexDirection="row" _justifyContent="center" _width={iconWidth}>
            <Icon _color="brand-primary-dark" _size="m" icon={iconUser} />
          </Box>
          <Box>
            <H3 i18n="contact" />
            <Text>{`${contact.firstName} ${contact.lastName}`}</Text>
            <Text>{contact.email}</Text>
            <Text>{contact.phoneNumber}</Text>
          </Box>
        </Box>
      </Box>
    </ColumnLayout>
  );

  const informationContainer = compact !== true && staticInformation;

  const compactInformationContainer = compact === true && (
    <ColumnLayout medium={[1, 1]}>
      <Box _display="flex" _flexDirection="column">
        {staticInformation}
      </Box>
      <OverviewBox form={form.watch()} referrer={props.referrer} />
    </ColumnLayout>
  );

  return (
    <TranslationProvider name="overview" type="component">
      <NewSection>
        <Section.Item headline="checkOrder" headlineIcon={faBallotCheck}>
          <Text
            i18n={{
              id: "description",
              values: {
                dayCount: getNeededRelocationDaysCount(
                  form.watch("articleType"),
                ),
              },
            }}
          />
        </Section.Item>
      </NewSection>
      <NewSection>
        <Section.Item>
          {informationContainer}
          {compactInformationContainer}
          <ConditionsCheckbox buttonText="bookButtonText" showPHPHint />
        </Section.Item>
      </NewSection>
    </TranslationProvider>
  );
};
